import { useState } from 'react';
import {
  AccountModel,
  DiaChi,
  DonViCoSo,
  HanThanhToan,
  KhachHang,
  type MauSoHoaDon,
  NgoaiTe,
  NhanVien,
  PhuongThucGiaoHang,
  PhuongTienVanChuyen,
  QuyenChungTu,
  TinhChatThue
} from '@/types/schemas';

export interface FormFieldState {
  // Customer information
  khachHang: KhachHang | null;
  nhanVien: NhanVien | null;
  taiKhoan: AccountModel | null;
  hanThanhToan: HanThanhToan | null;

  // Document information
  quyenChungTu: QuyenChungTu | null;
  ngoaiTe: NgoaiTe | null;
  donViCoSo: DonViCoSo | null;
  soChungTuHienTai: string;
  mauHoaDon: MauSoHoaDon | null;

  // Other information
  diaChi: DiaChi | null;
  phuongTienVanChuyen: PhuongTienVanChuyen | null;
  phuongTienGiaoHang: PhuongThucGiaoHang | null;
  coQuanThue: KhachHang | null;
  dienGiai: string;

  // Checkbox states
  pt_tao_yn: boolean;
  transfer_yn: boolean;
  ty_gia: number;
  tinhChatThue: TinhChatThue | null;
}

export interface FormFieldActions {
  // Search field setters
  setKhachHang: (khachHang: KhachHang) => void;
  setNhanVien: (nhanVien: NhanVien) => void;
  setTaiKhoan: (taiKhoan: AccountModel) => void;
  setHanThanhToan: (hanThanhToan: HanThanhToan) => void;
  setQuyenChungTu: (quyenChungTu: QuyenChungTu) => void;
  setNgoaiTe: (ngoaiTe: NgoaiTe) => void;
  setDonViCoSo: (donViCoSo: DonViCoSo) => void;
  setSoChungTuHienTai: (soChungTuHienTai: string) => void;
  setMauHoaDon: (mauHoaDon: MauSoHoaDon) => void;

  setDiaChi: (diaChi: DiaChi) => void;
  setPhuongTienVanChuyen: (phuongTienVanChuyen: PhuongTienVanChuyen) => void;
  setPhuongTienGiaoHang: (phuongTienGiaoHang: PhuongThucGiaoHang) => void;
  setCoQuanThue: (coQuanThue: KhachHang) => void;
  setDienGiai: (dienGiai: string) => void;

  // Checkbox setters
  setPtTaoYn: (ptTaoYn: boolean) => void;
  setTransferYn: (transferYn: boolean) => void;
  setTyGia: (tyGia: number) => void;
  setTinhChatThue: (tinhChatThue: TinhChatThue) => void;

  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<FormFieldState>) => void;
}

export interface UseFormFieldStateReturn {
  state: FormFieldState;
  actions: FormFieldActions;
}

const initialState: FormFieldState = {
  // Customer information
  khachHang: null,
  nhanVien: null,
  taiKhoan: null,
  hanThanhToan: null,

  // Document information
  quyenChungTu: null,
  ngoaiTe: null,
  donViCoSo: null,
  soChungTuHienTai: '',
  mauHoaDon: null,

  // Other information
  diaChi: null,
  phuongTienVanChuyen: null,
  phuongTienGiaoHang: null,
  coQuanThue: null,
  dienGiai: '',

  // Checkbox states
  pt_tao_yn: false,
  transfer_yn: false,
  ty_gia: 1,
  tinhChatThue: null
};

function transformInitialData(initialData?: BanHangRecord): Partial<FormFieldState> {
  if (!initialData) return {};

  return {
    // Customer information
    khachHang: initialData.ma_kh_data || null,
    nhanVien: initialData.ma_nv_data || null,
    taiKhoan: initialData.tk_data || null,
    hanThanhToan: initialData.han_tt_data || null,
    dienGiai: initialData.dien_giai || '',

    // Document information
    quyenChungTu: initialData.ma_nk_data || null,
    ngoaiTe: initialData.ma_nt_data || null,
    donViCoSo: initialData.unit_id_data || null,
    mauHoaDon: initialData.ma_mau_ct_data || null,

    soChungTuHienTai: initialData.so_ct || '',

    // Other information
    diaChi: initialData.ma_dc_data || null,
    phuongTienVanChuyen: initialData.ma_ptvc_data || null,
    phuongTienGiaoHang: initialData.ma_ptgh_data || null,
    coQuanThue: initialData.ma_kh9_data || null,

    // Checkbox states
    pt_tao_yn: initialData.pt_tao_yn || false,
    transfer_yn: initialData.transfer_yn || true,
    ty_gia: initialData.ty_gia || 1,
    tinhChatThue: initialData.ma_tc_thue_data || null
  };
}

export function useFormFieldState(initialData?: BanHangRecord): UseFormFieldStateReturn {
  const [state, setState] = useState<FormFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: FormFieldActions = {
    // Search field setters
    setKhachHang: (khachHang: KhachHang) => {
      setState(prev => ({
        ...prev,
        khachHang
      }));
    },

    setNhanVien: (nhanVien: NhanVien) => {
      setState(prev => ({
        ...prev,
        nhanVien
      }));
    },

    setTaiKhoan: (taiKhoan: AccountModel) => {
      setState(prev => ({
        ...prev,
        taiKhoan
      }));
    },

    setHanThanhToan: (hanThanhToan: HanThanhToan) => {
      setState(prev => ({
        ...prev,
        hanThanhToan
      }));
    },

    setQuyenChungTu: (quyenChungTu: QuyenChungTu) => {
      setState(prev => ({
        ...prev,
        quyenChungTu
      }));
    },

    setNgoaiTe: (ngoaiTe: NgoaiTe) => {
      setState(prev => ({
        ...prev,
        ngoaiTe
      }));
    },

    setDonViCoSo: (donViCoSo: DonViCoSo) => {
      setState(prev => ({
        ...prev,
        donViCoSo
      }));
    },

    setSoChungTuHienTai: (soChungTuHienTai: string) => {
      setState(prev => ({
        ...prev,
        soChungTuHienTai
      }));
    },

    setMauHoaDon: (mauHoaDon: MauSoHoaDon) => {
      setState(prev => ({
        ...prev,
        mauHoaDon
      }));
    },

    setDiaChi: (diaChi: DiaChi) => {
      setState(prev => ({
        ...prev,
        diaChi
      }));
    },

    setPhuongTienVanChuyen: (phuongTienVanChuyen: PhuongTienVanChuyen) => {
      setState(prev => ({
        ...prev,
        phuongTienVanChuyen
      }));
    },

    setPhuongTienGiaoHang: (phuongTienGiaoHang: PhuongThucGiaoHang) => {
      setState(prev => ({
        ...prev,
        phuongTienGiaoHang
      }));
    },

    setCoQuanThue: (coQuanThue: KhachHang) => {
      setState(prev => ({
        ...prev,
        coQuanThue
      }));
    },

    setDienGiai: (dienGiai: string) => {
      setState(prev => ({ ...prev, dienGiai }));
    },

    setPtTaoYn: (ptTaoYn: boolean) => {
      setState(prev => ({ ...prev, pt_tao_yn: ptTaoYn }));
    },

    setTransferYn: (transferYn: boolean) => {
      setState(prev => ({ ...prev, transfer_yn: transferYn }));
    },

    setTyGia: (tyGia: number) => {
      setState(prev => ({ ...prev, ty_gia: tyGia }));
    },

    setTinhChatThue: (tinhChatThue: TinhChatThue) => {
      setState(prev => ({ ...prev, tinhChatThue }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<FormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
